import React, { JSX, useCallback, useEffect, useRef, useState } from 'react'
import { Navigate } from 'react-router'
import {
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { useAuth } from 'context/user'
import { useEntitlements, tierLabel, planDisplayName } from 'context/plan'
import { useV1Visible } from 'hooks/useV1Visible'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import {
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGE_BYTES,
  assetS3KeyFromUpload,
  uploadImageAsset
} from 'components/ImageUpload'
import { AssetListPager, assetDisplayName, assetNameMatches, isReplacedAsset } from 'components/AssetPickerModal'
import { graphqlRequest } from 'lib/helpers'
import { QUICK_OVERLAYS_PATH } from 'lib/paths'
import {
  AssetReference,
  AssetReplaceResult,
  AssetSummary,
  DeletionResult,
  ImageUploadUrl
} from 'types/api'
import requestImageUploadUrlMutation from 'graphql/requestImageUploadUrl.graphql'
import confirmAssetUploadMutation from 'graphql/confirmAssetUpload.graphql'
import listMyAssetsQuery from 'graphql/listMyAssets.graphql'
import deleteAssetMutation from 'graphql/deleteAsset.graphql'
import replaceAssetReferencesMutation from 'graphql/replaceAssetReferences.graphql'

const MIB = 1024 * 1024
const GIB = 1024 * MIB

const round1 = (n: number): string => {
  const fixed = n.toFixed(1)
  return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed
}

const formatBytes = (bytes: number): string =>
  bytes >= GIB ? `${round1(bytes / GIB)} GB` : `${round1(bytes / MIB)} MB`

const capLabel = (cap: number | null): string =>
  cap === null ? 'Unlimited' : formatBytes(cap)

const REFERENCE_KIND_LABEL: Record<string, string> = {
  POLL: 'Poll',
  TUG: 'Tug of War',
  BOARD: 'Scoreboard',
  BOARDCOMP: 'Competitor',
  MERCH: 'Merch board'
}

const referenceKindLabel = (kind: string): string => {
  const known = REFERENCE_KIND_LABEL[kind]
  if (known !== undefined) return known
  const lower = kind.toLowerCase().replace(/_/g, ' ')
  return lower.length === 0 ? kind : lower.charAt(0).toUpperCase() + lower.slice(1)
}

const uploadDate = (createdAt: string): string => {
  const parsed = new Date(createdAt)
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString()
}

const imagePickProblem = (file: File): string | null => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'Images must be PNG, JPEG, WebP, or GIF.'
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return `Images must be ${formatBytes(MAX_IMAGE_BYTES)} or smaller.`
  }
  return null
}

const isReplaced = isReplacedAsset

const ASSETS_PAGE_SIZE = 20

const countOf = (n: number | null | undefined): number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0

const placesLabel = (n: number): string => `${n} place${n === 1 ? '' : 's'}`

interface ReplacePair {
  fromAssetId: string
  fromName: string
  toAssetId: string
  toName: string
}

interface ReplaceNotice {
  pair: ReplacePair
  result: AssetReplaceResult | null
  error: string | null
  placedSoFar: number
}

const RETRY_BUTTON_CLASSES = 'inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-500/40 px-2 py-1 text-[11px] font-semibold text-amber-200 hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-60'

const ReplaceNoticeBanner = ({
  notice,
  retrying,
  locked,
  onRetry,
  onDismiss
}: {
  notice: ReplaceNotice
  retrying: boolean
  locked: boolean
  onRetry: () => void
  onDismiss: () => void
}): JSX.Element => {
  const { pair, result, error, placedSoFar } = notice
  const complete = result?.complete === true

  let body: JSX.Element
  if (result === null) {
    body = (
      <>
        <p>
          &ldquo;{pair.toName}&rdquo; uploaded, but swapping it in for
          &ldquo;{pair.fromName}&rdquo; did not finish.
          {error !== null && error !== '' && <> {error}</>}
        </p>
        {placedSoFar > 0 && (
          <p className='mt-1 text-amber-300/80'>
            Already replaced in {placesLabel(placedSoFar)} by an earlier run.
          </p>
        )}
        <p className='mt-1 text-amber-300/80'>
          Retry is safe: it only updates places still using the old image.
        </p>
      </>
    )
  } else {
    const failed = countOf(result.failed)
    const skipped = countOf(result.skippedChanged)
    let failedLine: string | null = null
    if (!complete) {
      failedLine = failed > 0
        ? `${failed} could not be updated.`
        : 'Some updates did not finish.'
    }
    const unused = complete && placedSoFar === 0 && skipped === 0
    body = (
      <>
        <p>
          <span className='font-semibold'>
            Replaced in {placesLabel(placedSoFar)}{unused ? ' (nothing was using it)' : ''}.
          </span>
          {failedLine !== null && <> {failedLine}</>}
        </p>
        {skipped > 0 && (
          <p className='mt-1 opacity-80'>
            {skipped === 1
              ? '1 skipped because it was edited while the replace ran. It keeps that edit.'
              : `${skipped} skipped because they were edited while the replace ran. They keep those edits.`}
          </p>
        )}
        {complete
          ? (
            <p className='mt-1 opacity-80'>
              &ldquo;{pair.fromName}&rdquo; is kept in your library with a Replaced
              badge. Delete it yourself when you no longer need it.
            </p>
            )
          : (
            <p className='mt-1 opacity-80'>
              Retry is safe: it only updates places still using
              &ldquo;{pair.fromName}&rdquo;.
            </p>
            )}
      </>
    )
  }

  const toneClasses = complete
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
    : 'border-amber-500/30 bg-amber-500/10 text-amber-300'

  return (
    <div
      role='status'
      aria-live='polite'
      data-testid='replace-notice'
      className={`flex items-start gap-2 rounded-md border p-3 text-xs ${toneClasses}`}
    >
      {complete
        ? <CheckCircleIcon className='size-4 shrink-0' aria-hidden='true' />
        : <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />}
      <div className='min-w-0 flex-1'>{body}</div>
      {!complete && (
        <button
          type='button'
          onClick={onRetry}
          disabled={locked}
          data-testid='replace-retry-button'
          className={RETRY_BUTTON_CLASSES}
        >
          {retrying
            ? <LoadingSpinner />
            : <ArrowPathIcon className='size-3.5' aria-hidden='true' />}
          Retry
        </button>
      )}
      <button
        type='button'
        onClick={onDismiss}
        aria-label='Dismiss'
        title='Dismiss'
        className='-m-1 shrink-0 rounded p-1 opacity-70 transition-colors hover:bg-white/5 hover:opacity-100'
      >
        <XMarkIcon className='size-3.5' aria-hidden='true' />
      </button>
    </div>
  )
}

const StorageCard = (): JSX.Element => {
  const { status, tier, limitFor, upgradeForLimit, usage } = useEntitlements()

  const ready = status === 'ready'
  const cap = ready ? limitFor('assetStorageBytes') : null
  const upgrade = ready ? upgradeForLimit('assetStorageBytes', cap) : null
  const pct = cap === null || cap === 0
    ? 0
    : Math.min(100, (usage.storageBytes / cap) * 100)

  let usageLine: JSX.Element
  if (ready) {
    usageLine = (
      <span className='text-sm tabular-nums text-dm-text-secondary'>
        <span className='font-medium text-dm-text-primary'>{formatBytes(usage.storageBytes)}</span>
        {' '}of {capLabel(cap)}
      </span>
    )
  } else {
    usageLine = <span className='h-4 w-28 animate-pulse rounded bg-white/5' aria-hidden='true' />
  }

  return (
    <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
      <div className='flex items-baseline justify-between'>
        <h3 className='text-base font-semibold text-dm-text-primary'>Storage</h3>
        {usageLine}
      </div>
      {cap !== null && (
        <div className='mt-3 h-2 overflow-hidden rounded-full bg-white/5'>
          <div className='h-full rounded-full bg-brand' style={{ width: `${pct}%` }} />
        </div>
      )}
      {ready && tier !== null && (
        <p className='mt-2 text-[11px] text-dm-text-accent'>
          {tierLabel(tier)} plan
          {upgrade !== null && (
            <> · <span className='font-medium text-brand'>{planDisplayName(upgrade)} raises this to {capLabel(upgrade.limits.assetStorageBytes)}</span></>
          )}
        </p>
      )}
    </section>
  )
}

const ReferenceChip = ({ reference }: { reference: AssetReference }): JSX.Element => {
  const eventName = typeof reference.eventName === 'string' ? reference.eventName : ''
  const hasEvent = eventName !== ''
  const kindLabel = referenceKindLabel(reference.elementKind)
  return (
    <span
      className='inline-flex max-w-full items-center gap-1 truncate rounded-full border border-white/10 bg-dm-bg-accent/40 px-2 py-0.5 text-[10px] text-dm-text-secondary'
      title={hasEvent ? `${kindLabel} ${reference.elementName} · Stream ${eventName}` : `${kindLabel} ${reference.elementName}`}
    >
      <span className='shrink-0 font-semibold text-dm-text-accent'>
        {kindLabel}
      </span>
      <span className='truncate'>{reference.elementName}</span>
      {hasEvent && (
        <>
          <span aria-hidden='true' className='shrink-0 text-dm-text-accent/60'>·</span>
          <span className='shrink-0 text-dm-text-accent'>Stream</span>
          <span className='truncate'>{eventName}</span>
        </>
      )}
    </span>
  )
}

const MerchUseChip = ({ boardId }: { boardId: string }): JSX.Element => (
  <span
    className='inline-flex max-w-full items-center gap-1 truncate rounded-full border border-white/10 bg-dm-bg-accent/40 px-2 py-0.5 text-[10px] text-dm-text-secondary'
    title={`Merch board ${boardId}`}
  >
    <span className='shrink-0 font-semibold text-dm-text-accent'>Merch board</span>
    <span className='truncate'>{boardId.slice(0, 8)}</span>
  </span>
)

const merchUses = (asset: AssetSummary): string[] =>
  (asset.usedByMerchBoards ?? []).filter(
    (id): id is string => typeof id === 'string' && id !== ''
  )

type RowAction = 'delete' | 'replace'

const replacedLine = (asset: AssetSummary, replacedByName: string | null): string => {
  const when = typeof asset.replacedAt === 'string' ? uploadDate(asset.replacedAt) : ''
  const by = replacedByName !== null ? ` by "${replacedByName}"` : ''
  const on = when !== '' ? ` on ${when}` : ''
  return `Replaced${by}${on}.`
}

const AssetRow = ({
  asset,
  busyAction,
  locked,
  selected,
  replacedByName,
  replacesName,
  onDelete,
  onReplace,
  onToggleSelect
}: {
  asset: AssetSummary
  busyAction: RowAction | null
  locked: boolean
  selected: boolean
  replacedByName: string | null
  replacesName: string | null
  onDelete: () => void
  onReplace: () => void
  onToggleSelect: (checked: boolean) => void
}): JSX.Element => {
  const replaced = isReplaced(asset)
  const inUse = asset.references.length > 0 || merchUses(asset).length > 0
  const showReplace = !replaced || inUse
  return (
    <li
      className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2.5'
      data-testid={replaced ? 'replaced-asset-row' : 'asset-row'}
    >
      <div className='flex items-center gap-3'>
        <input
          type='checkbox'
          checked={selected}
          onChange={(e) => onToggleSelect(e.target.checked)}
          disabled={locked}
          aria-label={`Select ${assetDisplayName(asset)}`}
          data-testid='asset-select-checkbox'
          className='size-4 shrink-0 cursor-pointer rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60'
        />
        <div className={`h-12 w-12 shrink-0 overflow-hidden rounded-md border border-white/10 bg-dm-bg-accent/50${replaced ? ' opacity-60' : ''}`}>
          <img
            src={asset.publicUrl}
            alt={assetDisplayName(asset)}
            loading='lazy'
            className='size-full object-cover'
            onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2' }}
          />
        </div>
        <div className='flex min-w-0 flex-1 flex-col gap-1 leading-tight'>
          <div className='flex flex-wrap items-baseline gap-x-3 gap-y-0.5'>
            <span className='truncate text-sm font-medium text-dm-text-primary' title={assetDisplayName(asset)}>
              {assetDisplayName(asset)}
            </span>
            {replaced && (
              <span className='shrink-0 rounded-full border border-white/10 bg-white/5 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
                Replaced
              </span>
            )}
            <span className='shrink-0 text-[11px] tabular-nums text-dm-text-accent'>
              Uploaded {uploadDate(asset.createdAt)}
            </span>
            <span className='shrink-0 text-[11px] tabular-nums text-dm-text-accent'>
              {formatBytes(asset.sizeBytes)}
            </span>
          </div>
          {replaced && (
            <span className='text-[11px] text-dm-text-accent'>
              {replacedLine(asset, replacedByName)}
              {!inUse && ' No longer used anywhere.'}
            </span>
          )}
          {!replaced && replacesName !== null && (
            <span className='text-[11px] text-dm-text-accent'>
              Replaces &ldquo;{replacesName}&rdquo;
            </span>
          )}
          {inUse && (
            <div className='flex flex-wrap gap-1'>
              {asset.references.map((r) => (
                <ReferenceChip key={`${r.elementKind}-${r.elementId}`} reference={r} />
              ))}
              {merchUses(asset).map((boardId) => (
                <MerchUseChip key={boardId} boardId={boardId} />
              ))}
            </div>
          )}
        </div>
        {showReplace && (
          <button
            type='button'
            onClick={onReplace}
            disabled={locked}
            title='Replace image'
            data-testid='replace-asset-button'
            className='shrink-0 rounded-md p-1.5 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busyAction === 'replace'
              ? <LoadingSpinner />
              : <ArrowPathRoundedSquareIcon className='size-4' aria-hidden='true' />}
            <span className='sr-only'>Replace image</span>
          </button>
        )}
        <button
          type='button'
          onClick={onDelete}
          disabled={locked}
          title='Delete image'
          className='shrink-0 rounded-md p-1.5 text-dm-text-accent hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {busyAction === 'delete' ? <LoadingSpinner /> : <TrashIcon className='size-4' aria-hidden='true' />}
          <span className='sr-only'>Delete image</span>
        </button>
      </div>
    </li>
  )
}

const BULK_DELETE_WORKERS = 3

const BULK_CONFIRM_NAMES = 5

interface BulkFailure {
  assetId: string
  name: string
  message: string
}

interface BulkOutcome {
  deleted: number
  alreadyGone: number
  failed: BulkFailure[]
}

const imagesLabel = (n: number): string => `${n} image${n === 1 ? '' : 's'}`

const bulkDeleteConfirmCopy = (targets: AssetSummary[]): string => {
  const n = targets.length
  const pronoun = n === 1 ? 'it' : 'them'
  const names = targets.map((a) => `"${assetDisplayName(a)}"`)
  const shown = names.slice(0, BULK_CONFIRM_NAMES)
  const more = names.length - shown.length
  const list = shown.join('\n') + (more > 0 ? `\nand ${more} more` : '')
  const elementKeys = new Set(
    targets.flatMap((a) => a.references.map((r) => `${r.elementKind}:${r.elementId}`))
  )
  const boards = new Set(targets.flatMap(merchUses))
  let cascadeLine: string
  if (elementKeys.size > 0 || boards.size > 0) {
    const parts: string[] = []
    if (elementKeys.size > 0) {
      parts.push(`removes ${pronoun} from ${elementKeys.size} element${elementKeys.size === 1 ? '' : 's'} still using ${pronoun} (their image field is cleared)`)
    }
    if (boards.size > 0) {
      parts.push(`leaves ${boards.size} merch board${boards.size === 1 ? '' : 's'} pointing at a deleted image until you edit ${boards.size === 1 ? 'it' : 'them'}`)
    }
    cascadeLine = `This also ${parts.join(', and ')}.`
  } else {
    cascadeLine = `No polls, tug-of-wars, scoreboards or merch boards currently use ${pronoun}.`
  }
  return `Delete ${imagesLabel(n)}?\n\n${list}\n\n${cascadeLine}\n\nThis cannot be undone.`
}

const SelectAllCheckbox = ({
  label,
  ids,
  selected,
  disabled,
  testId,
  onChange
}: {
  label: string
  ids: string[]
  selected: Set<string>
  disabled: boolean
  testId: string
  onChange: (ids: string[], select: boolean) => void
}): JSX.Element => {
  const ref = useRef<HTMLInputElement | null>(null)
  const count = ids.filter((id) => selected.has(id)).length
  const all = ids.length > 0 && count === ids.length
  const some = count > 0 && !all
  useEffect(() => {
    if (ref.current !== null) ref.current.indeterminate = some
  }, [some])
  return (
    <label className='inline-flex cursor-pointer items-center gap-2 text-[11px] text-dm-text-secondary'>
      <input
        ref={ref}
        type='checkbox'
        checked={all}
        onChange={() => onChange(ids, !all)}
        disabled={disabled || ids.length === 0}
        data-testid={testId}
        className='size-4 shrink-0 cursor-pointer rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60'
      />
      {label}
    </label>
  )
}

const BulkOutcomeBanner = ({
  outcome,
  locked,
  onRetry,
  onDismiss
}: {
  outcome: BulkOutcome
  locked: boolean
  onRetry: () => void
  onDismiss: () => void
}): JSX.Element => {
  const failedCount = outcome.failed.length
  const clean = failedCount === 0
  const toneClasses = clean
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
    : 'border-amber-500/30 bg-amber-500/10 text-amber-300'
  return (
    <div
      role='status'
      aria-live='polite'
      data-testid='assets-bulk-outcome'
      className={`flex items-start gap-2 rounded-md border p-3 text-xs ${toneClasses}`}
    >
      {clean
        ? <CheckCircleIcon className='size-4 shrink-0' aria-hidden='true' />
        : <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />}
      <div className='min-w-0 flex-1'>
        <p>
          {outcome.deleted > 0 && <span className='font-semibold'>Deleted {imagesLabel(outcome.deleted)}.</span>}
          {outcome.alreadyGone > 0 && (
            <> {outcome.alreadyGone} {outcome.alreadyGone === 1 ? 'was' : 'were'} already gone.</>
          )}
          {failedCount > 0 && <> {failedCount} could not be deleted:</>}
        </p>
        {failedCount > 0 && (
          <ul className='mt-1 space-y-0.5'>
            {outcome.failed.map((f) => (
              <li key={f.assetId} className='break-words'>
                <span className='font-medium'>&ldquo;{f.name}&rdquo;</span>: {f.message}
              </li>
            ))}
          </ul>
        )}
        {failedCount > 0 && (
          <p className='mt-1 opacity-80'>
            The ones that failed stay selected. Retry deletes only those.
          </p>
        )}
      </div>
      {failedCount > 0 && (
        <button
          type='button'
          onClick={onRetry}
          disabled={locked}
          data-testid='assets-bulk-retry'
          className={RETRY_BUTTON_CLASSES}
        >
          <ArrowPathIcon className='size-3.5' aria-hidden='true' />
          Retry
        </button>
      )}
      <button
        type='button'
        onClick={onDismiss}
        aria-label='Dismiss'
        title='Dismiss'
        className='-m-1 shrink-0 rounded p-1 opacity-70 transition-colors hover:bg-white/5 hover:opacity-100'
      >
        <XMarkIcon className='size-3.5' aria-hidden='true' />
      </button>
    </div>
  )
}

export const AssetsPage = (): JSX.Element => {
  const { auth0Token } = useAuth()
  const { refetch } = useEntitlements()
  const v1Visible = useV1Visible()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [assets, setAssets] = useState<AssetSummary[] | null>(null)
  const [listBusy, setListBusy] = useState(false)
  const [listError, setListError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const [rowBusy, setRowBusy] = useState<{ assetId: string, action: RowAction } | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const replaceInputRef = useRef<HTMLInputElement | null>(null)
  const replaceTargetRef = useRef<AssetSummary | null>(null)
  const [replaceNotice, setReplaceNotice] = useState<ReplaceNotice | null>(null)

  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [bulk, setBulk] = useState<{ done: number, total: number } | null>(null)
  const [bulkOutcome, setBulkOutcome] = useState<BulkOutcome | null>(null)

  const loadAssets = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    setListBusy(true)
    setListError(null)
    try {
      const { assets: fetched } = await graphqlRequest<{ assets: AssetSummary[] }>(
        listMyAssetsQuery, {}, auth0Token
      )
      setAssets(fetched)
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load assets')
    } finally {
      setListBusy(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void loadAssets()
  }, [loadAssets])

  useEffect(() => {
    if (assets === null) return
    setSelected((prev) => {
      if (prev.size === 0) return prev
      const present = new Set(assets.map((a) => a.assetId))
      const next = new Set([...prev].filter((id) => present.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [assets])

  if (!v1Visible) return <Navigate to={QUICK_OVERLAYS_PATH} replace />

  const locked = rowBusy !== null || bulk !== null

  const handlePick = (): void => {
    fileInputRef.current?.click()
  }

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if ((file == null) || auth0Token === undefined) return

    const problem = imagePickProblem(file)
    if (problem !== null) {
      setUploadError(problem)
      return
    }

    setUploading(true)
    setUploadError(null)
    try {
      const { upload } = await graphqlRequest<{ upload: ImageUploadUrl }>(
        requestImageUploadUrlMutation,
        { contentType: file.type, contentLength: file.size },
        auth0Token
      )
      const res = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      })
      if (!res.ok) {
        const detail = await res.text().catch(() => '')
        throw new Error(`S3 upload failed: ${res.status} ${detail.slice(0, 200)}`)
      }
      const { asset } = await graphqlRequest<{ asset: Omit<AssetSummary, 'references'> | null }>(
        confirmAssetUploadMutation,
        { s3Key: assetS3KeyFromUpload(upload), fileName: file.name },
        auth0Token
      )
      if (asset !== null) {
        const withRefs: AssetSummary = { ...asset, references: [] }
        setAssets((prev) => prev === null ? [withRefs] : [withRefs, ...prev])
      }
      void refetch()
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (asset: AssetSummary): Promise<void> => {
    if (auth0Token === undefined || locked) return
    const refCount = asset.references.length
    const merchCount = merchUses(asset).length
    let cascadeLine: string
    if (refCount > 0 || merchCount > 0) {
      const parts: string[] = []
      if (refCount > 0) parts.push(`removes it from ${refCount} element${refCount === 1 ? '' : 's'} still using it (their image field is cleared)`)
      if (merchCount > 0) parts.push(`leaves ${merchCount} merch board${merchCount === 1 ? '' : 's'} pointing at the deleted image until you edit ${merchCount === 1 ? 'it' : 'them'}`)
      cascadeLine = `This also ${parts.join(', and ')}.`
    } else {
      cascadeLine = 'No polls, tug-of-wars, scoreboards or merch boards currently use it.'
    }
    const ok = globalThis.confirm(
      `Delete "${assetDisplayName(asset)}"?\n\n${cascadeLine}\n\nThis cannot be undone.`
    )
    if (!ok) return
    setRowBusy({ assetId: asset.assetId, action: 'delete' })
    setRowError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deleteAssetMutation, { assetId: asset.assetId }, auth0Token
      )
      if (result.deleted) {
        setAssets((prev) => prev === null ? prev : prev.filter((a) => a.assetId !== asset.assetId))
        void refetch()
      }
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete image')
    } finally {
      setRowBusy(null)
    }
  }

  const toggleSelected = (assetId: string, checked: boolean): void => {
    setSelected((prev) => {
      if (prev.has(assetId) === checked) return prev
      const next = new Set(prev)
      if (checked) next.add(assetId)
      else next.delete(assetId)
      return next
    })
  }

  const setManySelected = (ids: string[], select: boolean): void => {
    setSelected((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => { if (select) next.add(id); else next.delete(id) })
      return next
    })
  }

  const runBulkDelete = async (targets: AssetSummary[]): Promise<void> => {
    if (auth0Token === undefined || targets.length === 0) return
    const token = auth0Token
    const total = targets.length
    setBulk({ done: 0, total })
    setBulkOutcome(null)
    setRowError(null)
    const removed = new Set<string>()
    const failedById = new Map<string, BulkFailure>()
    let deleted = 0
    let alreadyGone = 0
    let next = 0
    let done = 0
    const worker = async (): Promise<void> => {
      while (next < total) {
        const asset = targets[next]
        next += 1
        try {
          const { result } = await graphqlRequest<{ result: DeletionResult }>(
            deleteAssetMutation, { assetId: asset.assetId }, token
          )
          if (result.deleted) deleted += 1
          else alreadyGone += 1
          removed.add(asset.assetId)
        } catch (err) {
          failedById.set(asset.assetId, {
            assetId: asset.assetId,
            name: assetDisplayName(asset),
            message: err instanceof Error && err.message !== '' ? err.message : 'The delete did not go through.'
          })
        }
        done += 1
        setBulk({ done, total })
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(BULK_DELETE_WORKERS, total) }, async () => await worker())
    )
    const failed = targets
      .map((a) => failedById.get(a.assetId))
      .filter((f): f is BulkFailure => f !== undefined)
    setAssets((prev) => prev === null ? prev : prev.filter((a) => !removed.has(a.assetId)))
    setSelected(new Set(failed.map((f) => f.assetId)))
    setBulkOutcome({ deleted, alreadyGone, failed })
    setBulk(null)
    void refetch()
    void loadAssets()
  }

  const handleBulkDelete = async (): Promise<void> => {
    if (auth0Token === undefined || locked || assets === null) return
    const targets = assets.filter((a) => selected.has(a.assetId))
    if (targets.length === 0) return
    if (!globalThis.confirm(bulkDeleteConfirmCopy(targets))) return
    await runBulkDelete(targets)
  }

  const handleBulkRetry = async (): Promise<void> => {
    if (locked || assets === null || bulkOutcome === null) return
    const failedIds = new Set(bulkOutcome.failed.map((f) => f.assetId))
    await runBulkDelete(assets.filter((a) => failedIds.has(a.assetId)))
  }

  const runReplace = async (pair: ReplacePair, priorPlaced: number): Promise<void> => {
    if (auth0Token === undefined) return
    setRowBusy({ assetId: pair.fromAssetId, action: 'replace' })
    try {
      const { result } = await graphqlRequest<{ result: AssetReplaceResult | null }>(
        replaceAssetReferencesMutation,
        { fromAssetId: pair.fromAssetId, toAssetId: pair.toAssetId },
        auth0Token
      )
      if (result === null) {
        setReplaceNotice({ pair, result: null, error: 'The server did not confirm the replace.', placedSoFar: priorPlaced })
      } else {
        setReplaceNotice({
          pair,
          result,
          error: null,
          placedSoFar: priorPlaced + countOf(result.repointed) + countOf(result.merchBoardsRepointed)
        })
      }
    } catch (err) {
      setReplaceNotice({
        pair,
        result: null,
        error: err instanceof Error ? err.message : 'The replace did not run.',
        placedSoFar: priorPlaced
      })
    } finally {
      setRowBusy(null)
    }
    void loadAssets()
  }

  const handleReplacePick = (asset: AssetSummary): void => {
    if (locked) return
    replaceTargetRef.current = asset
    replaceInputRef.current?.click()
  }

  const handleReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]
    e.target.value = ''
    const target = replaceTargetRef.current
    replaceTargetRef.current = null
    if (file == null || target === null || auth0Token === undefined || locked) return

    const problem = imagePickProblem(file)
    if (problem !== null) {
      setRowError(problem)
      return
    }

    setRowBusy({ assetId: target.assetId, action: 'replace' })
    setRowError(null)
    setReplaceNotice(null)
    let pair: ReplacePair
    try {
      const { upload, asset } = await uploadImageAsset(file, auth0Token)
      const toAssetId = asset?.assetId ?? upload.assetId ?? ''
      if (toAssetId === '') {
        throw new Error('The new image uploaded but was not registered. Refresh and try again.')
      }
      pair = {
        fromAssetId: target.assetId,
        fromName: assetDisplayName(target),
        toAssetId,
        toName: asset !== null && typeof asset.fileName === 'string' && asset.fileName !== ''
          ? asset.fileName
          : file.name
      }
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Upload failed')
      setRowBusy(null)
      return
    }
    void refetch()
    await runReplace(pair, 0)
  }

  let listContent: JSX.Element
  if (assets === null) {
    listContent = (
      <div className='flex items-center justify-center rounded-lg border border-dashed border-white/10 px-4 py-10'>
        <LoadingSpinner />
      </div>
    )
  } else if (assets.length === 0) {
    listContent = (
      <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
        <p>No images yet. Images you upload for polls, tug-of-wars and scoreboards appear here.</p>
      </div>
    )
  } else {
    const nameById = new Map(assets.map((a) => [a.assetId, assetDisplayName(a)]))
    const nameFor = (assetId: string | null | undefined): string | null =>
      typeof assetId === 'string' && assetId !== '' ? nameById.get(assetId) ?? null : null
    const liveAssets = assets.filter((a) => !isReplaced(a) && assetNameMatches(a, query))
    const replacedAssets = assets.filter((a) => isReplaced(a) && assetNameMatches(a, query))
    const livePageCount = Math.max(1, Math.ceil(liveAssets.length / ASSETS_PAGE_SIZE))
    const livePage = Math.min(page, livePageCount)
    const livePageItems = liveAssets.slice((livePage - 1) * ASSETS_PAGE_SIZE, livePage * ASSETS_PAGE_SIZE)
    const renderRow = (asset: AssetSummary): JSX.Element => (
      <AssetRow
        key={asset.assetId}
        asset={asset}
        busyAction={rowBusy !== null && rowBusy.assetId === asset.assetId ? rowBusy.action : null}
        locked={locked}
        selected={selected.has(asset.assetId)}
        replacedByName={nameFor(asset.replacedByAssetId)}
        replacesName={nameFor(asset.replacesAssetId)}
        onDelete={() => { void handleDelete(asset) }}
        onReplace={() => handleReplacePick(asset)}
        onToggleSelect={(checked) => toggleSelected(asset.assetId, checked)}
      />
    )
    listContent = (
      <div className='space-y-5'>
        {liveAssets.length === 0 && replacedAssets.length === 0 && (
          <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
            <p data-testid='assets-no-matches'>No images match that name.</p>
          </div>
        )}
        {liveAssets.length > 0 && (
          <div>
            <div className='mb-2 px-3'>
              <SelectAllCheckbox
                label='Select all on this page'
                ids={livePageItems.map((a) => a.assetId)}
                selected={selected}
                disabled={locked}
                testId='assets-select-page'
                onChange={setManySelected}
              />
            </div>
            <ul className='space-y-2' data-testid='assets-list'>
              {livePageItems.map(renderRow)}
            </ul>
            <AssetListPager
              page={livePage}
              pageCount={livePageCount}
              shownFrom={(livePage - 1) * ASSETS_PAGE_SIZE + 1}
              shownTo={(livePage - 1) * ASSETS_PAGE_SIZE + livePageItems.length}
              total={liveAssets.length}
              onPage={setPage}
            />
          </div>
        )}
        {replacedAssets.length > 0 && (
          <div data-testid='replaced-assets'>
            <div className='mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-1'>
              <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
                Replaced images
              </h4>
              <span className='text-[11px] tabular-nums text-dm-text-accent'>{replacedAssets.length}</span>
              <span className='ml-auto'>
                <SelectAllCheckbox
                  label='Select all replaced'
                  ids={replacedAssets.map((a) => a.assetId)}
                  selected={selected}
                  disabled={locked}
                  testId='assets-select-replaced'
                  onChange={setManySelected}
                />
              </span>
            </div>
            <p className='mb-2 text-[11px] text-dm-text-accent'>
              Each of these was swapped for a newer upload everywhere it was
              used, so nothing references it unless a row below says
              otherwise. They are kept, never deleted automatically. Delete
              one yourself once you are sure you no longer need it. Replace
              stays available only on one that something still uses.
            </p>
            <ul className='space-y-2'>
              {replacedAssets.map(renderRow)}
            </ul>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='flex flex-1' data-testid='assets-page'>
      <main className='flex min-w-0 flex-1 flex-col px-4 py-4'>
        <header className='mb-1 flex items-center gap-3'>
          <h1 className='text-2xl font-semibold text-dm-text-primary'>Assets</h1>
        </header>
        <p className='mb-4 text-sm text-dm-text-secondary'>
          Every image you upload lands here. Delete anything to free up space.
        </p>

        <div className='flex flex-col gap-4'>
          <StorageCard />

          <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
            <div className='mb-4 flex flex-wrap items-center justify-between gap-2'>
              <h3 className='text-base font-semibold text-dm-text-primary'>Images</h3>
              <div className='flex flex-wrap items-center gap-2'>
                <input
                  type='search'
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value)
                    setPage(1)
                    setSelected((prev) => prev.size === 0 ? prev : new Set())
                  }}
                  disabled={bulk !== null}
                  placeholder='Search by name'
                  aria-label='Search images by name'
                  data-testid='assets-search'
                  className='w-44 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-1.5 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand sm:w-56'
                />
                <button
                  type='button'
                  onClick={() => { void loadAssets() }}
                  disabled={listBusy || bulk !== null}
                  title='Refresh'
                  className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {listBusy ? <LoadingSpinner /> : <ArrowPathIcon className='size-3.5' aria-hidden='true' />}
                  Refresh
                </button>
                <input
                  ref={fileInputRef}
                  type='file'
                  accept={ALLOWED_IMAGE_TYPES.join(',')}
                  onChange={(e) => { void handleFile(e) }}
                  className='hidden'
                />
                <input
                  ref={replaceInputRef}
                  type='file'
                  accept={ALLOWED_IMAGE_TYPES.join(',')}
                  onChange={(e) => { void handleReplaceFile(e) }}
                  className='hidden'
                  data-testid='replace-asset-input'
                />
                <button
                  type='button'
                  data-testid='upload-asset-button'
                  onClick={handlePick}
                  disabled={uploading || bulk !== null}
                  className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {uploading
                    ? <><LoadingSpinner /> Uploading…</>
                    : <><ArrowUpTrayIcon className='size-3.5' aria-hidden='true' /> Upload</>}
                </button>
              </div>
            </div>

            {listError !== null && (
              <div className='mb-3'>
                <ErrorInline onDismiss={() => setListError(null)}>{listError}</ErrorInline>
              </div>
            )}
            {uploadError !== null && (
              <div className='mb-3'>
                <ErrorInline onDismiss={() => setUploadError(null)}>{uploadError}</ErrorInline>
              </div>
            )}
            {rowError !== null && (
              <div className='mb-3'>
                <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
              </div>
            )}
            {replaceNotice !== null && (
              <div className='mb-3'>
                <ReplaceNoticeBanner
                  notice={replaceNotice}
                  retrying={rowBusy !== null && rowBusy.action === 'replace' && rowBusy.assetId === replaceNotice.pair.fromAssetId}
                  locked={locked}
                  onRetry={() => {
                    if (locked) return
                    void runReplace(replaceNotice.pair, replaceNotice.placedSoFar)
                  }}
                  onDismiss={() => setReplaceNotice(null)}
                />
              </div>
            )}
            {bulkOutcome !== null && (
              <div className='mb-3'>
                <BulkOutcomeBanner
                  outcome={bulkOutcome}
                  locked={locked}
                  onRetry={() => { void handleBulkRetry() }}
                  onDismiss={() => setBulkOutcome(null)}
                />
              </div>
            )}
            {(selected.size > 0 || bulk !== null) && (
              <div
                data-testid='assets-bulk-toolbar'
                className='mb-3 flex flex-wrap items-center gap-2 rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-xs'
              >
                <span className='tabular-nums text-dm-text-secondary'>
                  {imagesLabel(selected.size)} selected
                </span>
                {bulk !== null && (
                  <span role='status' aria-live='polite' data-testid='assets-bulk-progress' className='tabular-nums text-dm-text-accent'>
                    Deleting: {bulk.done} of {bulk.total} done
                  </span>
                )}
                <div className='ml-auto flex flex-wrap items-center gap-2'>
                  <button
                    type='button'
                    onClick={() => setSelected(new Set())}
                    disabled={locked}
                    data-testid='assets-bulk-clear'
                    className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                  >
                    Clear selection
                  </button>
                  <button
                    type='button'
                    onClick={() => { void handleBulkDelete() }}
                    disabled={locked || selected.size === 0}
                    data-testid='assets-bulk-delete-button'
                    className='inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-2.5 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60'
                  >
                    {bulk !== null ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
                    Delete selected ({selected.size})
                  </button>
                </div>
              </div>
            )}

            {listContent}

            <p className='mt-4 text-[11px] text-dm-text-accent'>
              ≤ {formatBytes(MAX_IMAGE_BYTES)} per image: PNG, JPEG, WebP, or
              GIF. Deleting an image also clears it from any poll, tug-of-war
              or scoreboard still using it (shown as chips on each row).
              Replacing uploads a new image, swaps it in everywhere the old
              one is used (merch boards included), and keeps the old one
              here, marked Replaced once every place is updated.
            </p>
          </section>
        </div>
      </main>
    </div>
  )
}
